import { Box, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { TransferFragment } from 'types/api/tickets.types';

import { IntlCopy, InvalidTransferVariant } from './types';
import { isPrivateRelayEmail } from './util';

const INTL_COPY: Record<InvalidTransferVariant, IntlCopy> = {
  completed: {
    title: 'transfer_tickets.invalid.accepted.title',
    description: 'transfer_tickets.invalid.accepted.description',
  },
  cancelled: {
    title: 'transfer_tickets.invalid.cancelled.title',
    description: 'transfer_tickets.invalid.cancelled.description',
  },
  expired: {
    title: 'transfer_tickets.invalid.event_passed.title',
    description: 'transfer_tickets.invalid.event_passed.description',
  },
  fallback: {
    title: 'transfer_tickets.invalid.fallback.title',
    description: 'transfer_tickets.invalid.fallback.description',
  },
};

interface Props {
  variant: InvalidTransferVariant;
  transfer: TransferFragment | undefined;
}

const InvalidTransferVariants = ({ variant, transfer }: Props) => {
  const intlCopy = INTL_COPY[variant];

  return (
    <>
      <Typography
        variant="h6"
        component="h1"
        sx={{
          mb: 1,
        }}
      >
        <FormattedMessage id={intlCopy.title} />
      </Typography>
      <Typography variant="body2">
        <FormattedMessage
          id={intlCopy.description}
          values={{
            br: <br />,
          }}
        />
      </Typography>
      {!!transfer?.id && (
        <Stack
          sx={{
            bgcolor: 'grey.900',
            border: '1px solid',
            borderColor: 'grey.800',
            borderRadius: 4,
            p: 2,
            my: 3,
            gap: 0.5,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
            }}
          >
            <FormattedMessage id="transfer_tickets.invalid.details.label" />
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              py: '2px',
            }}
          >
            <Typography
              variant="small"
              sx={{
                color: 'grey.400',
              }}
            >
              <FormattedMessage id="transfer_tickets.invalid.details.id.label" />
            </Typography>
            <Typography
              variant="small"
              sx={{
                color: 'grey.400',
              }}
            >
              ******{transfer?.id?.slice(-4)}
            </Typography>
          </Box>
          <TransferDetailsPersonalInfo transfer={transfer} variant={variant} />
        </Stack>
      )}
    </>
  );
};

export default InvalidTransferVariants;

const getPersonalDetails = (
  variant: InvalidTransferVariant,
  transfer: TransferFragment,
) => {
  const isCompleted = variant === 'completed';

  const email = isCompleted ? transfer?.receiverEmail : transfer?.senderEmail;

  return {
    name: isCompleted ? transfer?.receiverName : transfer?.senderName,
    email,
    title: isCompleted
      ? 'transfer_tickets.invalid.details.accepted_by.label'
      : variant === 'cancelled'
        ? 'transfer_tickets.invalid.details.cancelled_by.label'
        : 'transfer_tickets.invalid.details.created_by.label',
  };
};

const TransferDetailsPersonalInfo = ({ transfer, variant }: Props) => {
  if (variant === 'expired' || !transfer) return null;

  const { name, email, title } = getPersonalDetails(variant, transfer);

  if (!name && !email) return null;

  return (
    <Stack>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          pt: '2px',
        }}
      >
        <Typography
          variant="smallParagraph"
          sx={{
            color: 'grey.400',
          }}
        >
          <FormattedMessage id={title} />
        </Typography>
        {!!name && (
          <Typography
            variant="smallParagraph"
            sx={{
              color: 'grey.400',
            }}
          >
            {name}
          </Typography>
        )}
        {!name && !!email && <EmailInfo email={email} />}
      </Box>
      {!!name && !!email && <EmailInfo email={email} />}
    </Stack>
  );
};

const EmailInfo = ({ email }: { email: string }) => {
  return (
    <Stack
      sx={{
        alignItems: 'flex-end',
        pb: '2px',
      }}
    >
      <Typography
        variant="smallParagraph"
        sx={{
          color: 'grey.400',
        }}
      >
        {email}
      </Typography>
      {isPrivateRelayEmail(email) && (
        <Typography
          variant="smallParagraph"
          sx={{
            fontWeight: 'bold',
            mt: 0.5,
          }}
        >
          <FormattedMessage id="transfer_tickets.invalid.details.apple_account" />
        </Typography>
      )}
    </Stack>
  );
};
